import { FC, useCallback } from 'react';

import { twMerge } from 'tailwind-merge';

import { Skeleton } from '../../../..';
import { closeModal } from '../../../../../lib';
import { EmptyState } from '../../../../empty-state';
import { ModalTemplate } from '../../../../templates';
import { InfinityScroll } from '../../../infinity-scroll';
import { Input } from '../../../input';
import { SelectModalProps, SelectOption } from '../../types';
import { TSelectOptionItem } from './types';

const SelectOptionItem: FC<TSelectOptionItem> = ({
  isActive,
  className,
  children,
}) => (
  <div
    data-is-active={isActive}
    className={twMerge(
      'flex h-max w-full items-center transition-colors data-[is-active="true"]:bg-base-300',
      className,
    )}
  >
    {children}
  </div>
);

export const SelectModalWrapper = <TOption,>({
  modalId,
  value,
  onSelect,
  options,
  renderOptionItem,
  searchProps = {},
  infinityProps,
  listProps,
  searchValue,
  onSearchChange,
  CustomEmptyState,
  CustomSkeletons,
  classNames = {},
  modalProps = {},
  isMulti,
}: SelectModalProps<TOption>) => {
  const selectOptionHandler = useCallback(
    (option: SelectOption<TOption>) =>
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        let newValue: SelectOption<TOption>[] = [];
        if (isMulti) {
          const currentValue = value.filter((v) => v.value !== option.value);
          if (currentValue.length !== value.length) {
            newValue = currentValue;
          } else {
            newValue = [...currentValue, option];
          }
        } else {
          newValue = [option];
        }

        onSelect(newValue);

        if (!isMulti) closeModal(modalId);
      },
    [value, onSelect, isMulti, modalId],
  );

  const renderContent = () => {
    if (!options.length && !infinityProps?.isLoading) {
      return (
        CustomEmptyState ?? (
          <EmptyState description={listProps?.descriptionEmptyState} />
        )
      );
    }

    return (
      <InfinityScroll
        isLoading={infinityProps?.isLoading && !options.length}
        onLoadMore={infinityProps?.next}
        hasMore={infinityProps?.hasNext ?? false}
        className={twMerge(
          listProps?.size === 'sm' ? 'px-1' : 'px-4 pt-1',
          listProps?.className,
        )}
        isLoadingMore={infinityProps?.isLoadingMore ?? false}
        CustomSkeletons={Array.from({ length: 10 }).map((_, index) => (
          <SelectOptionItem
            key={index}
            className={twMerge(
              listProps?.size === 'sm'
                ? 'rounded-[16px] p-4'
                : 'rounded-[10px] p-3',
              classNames.option,
            )}
          >
            {CustomSkeletons ?? <Skeleton className="h-10 w-full" />}
          </SelectOptionItem>
        ))}
      >
        {options.map((option) => (
          <SelectOptionItem
            key={option.value}
            isActive={value?.some((v) => v.value === option.value) ?? false}
            className={twMerge(
              'hover:bg-base-300',
              listProps?.size === 'sm' ? 'rounded-[16px]' : 'rounded-[10px]',
              classNames.option,
            )}
          >
            <button
              className={twMerge(
                'w-full text-left',
                listProps?.size === 'sm' ? 'p-4' : 'p-3',
              )}
              type="button"
              onClick={selectOptionHandler(option)}
            >
              {renderOptionItem ? renderOptionItem(option) : option.label}
            </button>
          </SelectOptionItem>
        ))}
      </InfinityScroll>
    );
  };

  return (
    <ModalTemplate
      {...modalProps}
      id={modalId}
      className={twMerge(
        'flex min-h-0 flex-col overflow-hidden',
        modalProps?.className,
      )}
    >
      {onSearchChange && (
        <div className={twMerge('shrink-0 p-4', searchProps?.wrapperClassName)}>
          <Input
            value={searchValue}
            onValueChange={onSearchChange}
            placeholder={searchProps?.placeholder ?? 'Search'}
            variant={searchProps?.variant ?? 'dashboard'}
            {...searchProps}
          />
        </div>
      )}
      {renderContent()}
    </ModalTemplate>
  );
};
